var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { css, styled } from '../theme-provider';
import { Text, TokenTextAppearance, TokenTextColor, TextTag } from '../text';
import { createStylesForBreakpoints, } from '../helpers/breakpoint';
import { createA11yOutlineStyles } from '../helpers/a11y';
const isLink = (props) => Boolean(props.tag === 'a');
const isAccentButton = (props) => props.emphasis === 'offer' ||
    props.emphasis === 'nbab';
const getTokenColors = (theme, disabled, isIconOnly) => {
    return {
        primary: `
			color: ${theme.colors.content[disabled ? 'tertiary' : 'inverted']};
			background: ${theme.colors.button.primary[disabled ? 'disabled' : 'default']};
			border:none;

			:hover:not(:disabled), :active:not(:disabled),:focus:not(:disabled):not(:focus-visible) {
				background: ${theme.colors.button.primary.active};
			}

			:focus-visible {
				${createA11yOutlineStyles(theme, 'solid')};
				outline-offset: unset;
			}
		`,
        secondary: `

			color: ${theme.colors.button.secondary[disabled ? 'disabled' : 'default']};
			background: transparent;
			border: 1px solid ${theme.colors.button.secondary[disabled ? 'disabled' : 'default']};

			:hover:not(:disabled), :active:not(:disabled),:focus:not(:disabled):not(:focus-visible) {
				color: ${theme.colors.content.inverted};
				background: ${theme.colors.button.secondary.active};
				border: 1px solid ${theme.colors.button.primary.active};
			}

			:focus-visible {
				${createA11yOutlineStyles(theme, 'solid')};
				outline-offset: unset;
			}
			`,
        tertiary: `
			color: ${theme.colors.button.tertiary[disabled ? 'disabled' : 'default']};
			background: transparent;
			border: none;

			:hover:not(:disabled), :active:not(:disabled),:focus:not(:disabled):not(:focus-visible)  {
				color: ${theme.colors.button.tertiary.active};
				background: transparent;

				span {
					text-decoration-thickness: ${theme.size.border.heavy};
				}
			}

			:focus-visible {
				${createA11yOutlineStyles(theme, 'solid')};
				outline-offset: ${isIconOnly ? 'unset' : '1px'};
			}
			`,
        offer: `
			color: ${theme.colors.content.invertedOnSurface};
			background: ${theme.colors.accent.offer.default};
			`,
        nbab: `
			color: ${theme.colors.content.invertedOnSurface};
			background: ${theme.colors.accent.nbab.default};
			`,
    };
};
// styled component for icons in links
const StyledLinkIcon = styled.span.withConfig({ displayName: "StyledLinkIcon", componentId: "sc-wjrax8" }) `line-height:0;position:relative;order:${(props) => (props.iconPosition === 'end' ? 1 : 'initial')};${({ iconPosition, theme }) => iconPosition === 'top' &&
    css `
			display: flex;
			justify-content: center;
			align-items: center;
			width: 44px;
			height: 44px;
			border-radius: 50%;
			border: ${theme.size.border.light} solid currentColor;
			margin-bottom: ${theme.size.static100};
		`}`;
// styled component for icons in buttons
const StyledButtonIcon = styled.span.withConfig({ displayName: "StyledButtonIcon", componentId: "sc-bk7es1" }) `line-height:0;order:${(props) => (props.iconPosition === 'end' ? 1 : 'initial')};`;
// styled component for (text-) children in links
const StyledLinkText = styled.span.withConfig({ displayName: "StyledLinkText", componentId: "sc-12fkfup" }) `position:relative;text-decoration:underline;text-underline-offset:0.2em;text-decoration-thickness:1px;${(props) => props.emphasis === 'none' && 'text-decoration: none;'} ${(props) => props.disabled
    ? css `
					pointer-events: none;
					cursor: not-allowed;
				`
    : css `
					cursor: pointer;
				`}`;
// styled component for arranging icons and children in buttons (emphasis primary and secondary, size small and large)
const StyledButtonWrapper = styled.div.withConfig({ displayName: "StyledButtonWrapper", componentId: "sc-grwi6b" }) `align-items:center;${(props) => {
    if (props.iconPosition === 'top') {
        return css `
				display: flex;
				flex-direction: column;
			`;
    }
    const templateColumns = props.iconPosition === 'end'
        ? `repeat(
					${React.Children.count(props.children) - 1},
					auto
				) max-content`
        : `max-content repeat(
					${React.Children.count(props.children) - 1},
					auto
				)`;
    return css `
			display: inline-grid;
			grid-template-columns: ${templateColumns};
			grid-column-gap: ${props.theme.size.static100};
		`;
}}`;
// styled component for arranging icons and children in links (emphasis tertiary)
const StyledLinkWrapper = styled.div.withConfig({ displayName: "StyledLinkWrapper", componentId: "sc-x60dy3" }) `align-items:center;${(props) => {
    if (props.iconPosition === 'top') {
        return css `
				display: flex;
				flex-direction: column;
			`;
    }
    return css `
			display: inline-grid;
			grid-template-columns: max-content auto;
			grid-column-gap: ${(props) => props.theme.size.static100};
			align-items: center;
		`;
}}`;
const createStretchContentForBreakpoints = (props) => {
    if (!props.stretchContent) {
        return '';
    }
    return createStylesForBreakpoints(props.stretchContent, (stretchContent) => (stretchContent ? 'width: 100%;' : 'width: initial;'));
};
// styled component for large buttons (without icon, emphasis primary and secondary, offer and nbab)
const StyledButton = styled.button.withConfig({ displayName: "StyledButton", componentId: "sc-1208ax7" }) `position:relative;${createStretchContentForBreakpoints} display:inline-flex;text-align:center;justify-content:center;align-items:center;${(props) => {
    const width = props.size === 'small' ? props.theme.size.static400 : '44px';
    const padding = props.size === 'small'
        ? `${props.theme.size.static100} ${props.theme.size.static250}`
        : `0 ${props.theme.size.static400}`;
    return css `
			min-height: ${width};
			min-width: ${width};
			${props.circular
        ? css `
						width: ${width};
						padding: 0;
					`
        : css `
						padding: ${padding};
					`}
			border: ${props.theme.size.border.light} solid;
		`;
}} margin:0;border-radius:500px;transition:all 0.2s ease-in-out;text-decoration:none;cursor:${(props) => (props.disabled ? 'not-allowed' : 'pointer')};-webkit-tap-highlight-color:transparent;${({ theme, disabled, emphasis, isIconOnly }) => emphasis &&
    getTokenColors(theme, disabled, isIconOnly)[emphasis === 'none' ? 'tertiary' : emphasis]} ${({ theme, emphasis }) => (emphasis === 'nbab' || emphasis === 'offer') &&
    css `
			border: none;

			:hover {
				color: ${theme.colors.content.inverted};
				background: ${theme.colors.accent.offer.hover};
			}

			:active,
			:focus:not(:focus-visible) {
				color: ${theme.colors.content.inverted};
				background: ${theme.colors.accent.offer.active};
			}

			:focus-visible {
				outline: 3px solid ${theme.colors.focus.accent};
				outline-offset: unset;
			}
		`}`;
// styled component for links (with or without icons, emphasis tertiary)
const StyledLink = styled.a.withConfig({ displayName: "StyledLink", componentId: "sc-afbv6g" }) `font-weight:inherit;display:inline-grid;${createStretchContentForBreakpoints} border:none;position:relative;text-decoration:none;cursor:${(props) => (props.disabled ? 'not-allowed' : 'pointer')};padding:0;text-align:inherit;${(props) => props.circular &&
    css `
			display: inline-flex;
			align-items: center;
			text-align: center;
			justify-content: center;
			width: ${props.size === 'small' ? props.theme.size.static400 : '44px'};
			height: ${props.size === 'small' ? props.theme.size.static400 : '44px'};
			border: ${props.theme.size.border.light} solid transparent;
			border-radius: 50%;
		`} ${({ theme, disabled, emphasis, isIconOnly }) => emphasis &&
    getTokenColors(theme, disabled, isIconOnly)[emphasis === 'none' ? 'tertiary' : emphasis]} -webkit-tap-highlight-color:transparent;`;
// Component for a CTA that looks like a link. Please note that it could either be a 'button' or an anchor link ('a') depending on its props.
const CTALink = (props) => {
    const { ariaLabel, children, disabled, emphasis, forwardRef, icon, iconPosition, size, tag, onClick } = props, rest = __rest(props, ["ariaLabel", "children", "disabled", "emphasis", "forwardRef", "icon", "iconPosition", "size", "tag", "onClick"]);
    const hasChildren = React.Children.count(children) > 0;
    // depending on the tag we need different props and different types for these props
    const additionalProps = isLink(props) && !disabled ? { href: props.href } : {};
    // biome-ignore lint/suspicious/noExplicitAny: there is no sane type for the ref of both StyledLink and StyledButton using "as" that works with both components and both tags, so we have to use "any" instead
    const ref = forwardRef;
    return (React.createElement(StyledLink, Object.assign({ as: tag, "aria-label": ariaLabel, disabled: disabled }, rest, additionalProps, { ref: ref, emphasis: emphasis, circular: icon && !hasChildren, isIconOnly: icon && !hasChildren, size: size, onClick: onClick, iconPosition: iconPosition }),
        icon && hasChildren && (React.createElement(StyledLinkWrapper, { iconPosition: iconPosition },
            React.createElement(StyledLinkIcon, { iconPosition: iconPosition }, icon),
            React.createElement("span", null,
                React.createElement(StyledLinkText, { emphasis: emphasis, disabled: disabled }, children)))),
        !hasChildren && icon,
        hasChildren && !icon && (React.createElement(StyledLinkText, { emphasis: emphasis, disabled: disabled }, children))));
};
// Component for a CTA that looks like a button. Please note that it could either be a 'button' or an anchor link ('a') depending on its props.
const CTAButton = (props) => {
    const { ariaLabel, children, forwardRef, icon, iconPosition, size = 'large', tag, onClick } = props, rest = __rest(props, ["ariaLabel", "children", "forwardRef", "icon", "iconPosition", "size", "tag", "onClick"]);
    const hasChildren = React.Children.count(children) > 0;
    // depending on the tag we need different props and different types for these props
    let additionalProps = {};
    if (isLink(props) && !props.disabled) {
        additionalProps = Object.assign(Object.assign({}, additionalProps), { href: props.href });
    }
    if (!isAccentButton(props)) {
        additionalProps = Object.assign(Object.assign({}, additionalProps), { disabled: props.disabled });
    }
    // biome-ignore lint/suspicious/noExplicitAny: there is no sane type for the ref of both StyledLink and StyledButton using "as" that works with both components and both tags, so we have to use "any" instead
    const ref = forwardRef;
    return (React.createElement(StyledButton, Object.assign({ as: tag, "aria-label": ariaLabel }, rest, additionalProps, { ref: ref, circular: icon && !hasChildren, isIconOnly: icon && !hasChildren, size: size, onClick: onClick }),
        icon && !hasChildren && icon,
        icon && hasChildren && (React.createElement(StyledButtonWrapper, { iconPosition: iconPosition },
            React.createElement(StyledButtonIcon, { iconPosition: iconPosition }, icon),
            React.createElement(Text, { appearance: TokenTextAppearance.label200, color: TokenTextColor.inherit, staticSize: true, tag: TextTag.span }, children))),
        hasChildren && !icon && (React.createElement(Text, { appearance: TokenTextAppearance.label200, color: TokenTextColor.inherit, tag: TextTag.span, staticSize: true }, children))));
};
export const CTA = React.forwardRef((props, forwardRef) => {
    var _a, _b, _c, _d;
    if (isAccentButton(props)) {
        return (React.createElement(CTAButton, Object.assign({}, props, { emphasis: props.emphasis, size: (_a = props.size) !== null && _a !== void 0 ? _a : 'large', forwardRef: forwardRef })));
    }
    if (props.emphasis === 'tertiary' || props.emphasis === 'none') {
        return (React.createElement(CTALink, Object.assign({}, props, { emphasis: props.emphasis, size: (_b = props.size) !== null && _b !== void 0 ? _b : 'large', forwardRef: forwardRef })));
    }
    return (React.createElement(CTAButton, Object.assign({}, props, { emphasis: (_c = props.emphasis) !== null && _c !== void 0 ? _c : 'primary', size: (_d = props.size) !== null && _d !== void 0 ? _d : 'large', forwardRef: forwardRef })));
});
CTA.displayName = 'CTA';
